import React from "react";

import { Typography } from "@material-ui/core";
import { DateTime } from "luxon";

import { useTranslation } from "react-i18next";

import { AvailableOrderTypes } from "api/types/enums";
import config from "config";
import makeStyles from "hooks/makeStyles";
import useBusiness from "hooks/useBusiness";
import useCart from "hooks/useCart";

const useStyles = makeStyles(theme => ({
	label: {
		fontWeight: "normal",
		textTransform: "uppercase",
		fontSize: "12px",
		[theme.breakpoints.down(330)]: {
			fontSize: 11,
			maxWidth: 130,
			overflow: "auto",
			textOverflow: "ellipsis"
		}
	},
	dateTime: {
		fontWeight: 500,
		textTransform: "uppercase",
		fontSize: "12px"
	}
}));

interface Props {
	isDesktop?: boolean;
}

const ReadyTimeMessage: React.FC<Props> = ({ isDesktop = false }) => {
	const { business } = useBusiness();
	const { getEffectiveDeliveryOrPickupAt, getDisplayDeliveryOrPickupAt, orderType } = useCart();
	const { t } = useTranslation();
	const classes = useStyles();

	const today = DateTime.utc().setZone(business.timeZone).startOf("day");

	let message: string;

	const effectiveDeliveryOrPickupAt = getEffectiveDeliveryOrPickupAt();
	const displayDeliveryOrPickupAt = getDisplayDeliveryOrPickupAt();

	if (effectiveDeliveryOrPickupAt) {
		// selected time
		if (effectiveDeliveryOrPickupAt.hasSame(today, "day")) {
			message = t(orderType.orderType === AvailableOrderTypes.pickup ? "readyToday" : "deliveryToday", {
				time: effectiveDeliveryOrPickupAt.toFormat("h:mm a")
			});
		} else {
			message = t(orderType.orderType === AvailableOrderTypes.pickup ? "readyNext" : "deliveryNext", {
				date: effectiveDeliveryOrPickupAt.toFormat("MMMM d"),
				time: effectiveDeliveryOrPickupAt.toFormat("h:mm a")
			});
		}
	} else {
		// ASAP
		if (displayDeliveryOrPickupAt) {
			if (displayDeliveryOrPickupAt.hasSame(today, "day")) {
				const minTime = Math.ceil(displayDeliveryOrPickupAt.diffNow("minutes").minutes / 5) * 5;
				if (minTime <= config.readyTimeMaxDisplayMinutes) {
					const maxTime = minTime + 10; // TODO: should be configurable
					message = isDesktop
						? t("readyIn", { minTime, maxTime })
						: t("asapIn", {
								minTime,
								maxTime
						  });
				} else {
					message = t(orderType.orderType === AvailableOrderTypes.pickup ? "readyToday" : "deliveryToday", {
						time: displayDeliveryOrPickupAt.toFormat("h:mm a")
					});
				}
			} else {
				message = t(orderType.orderType === AvailableOrderTypes.pickup ? "readyNext" : "deliveryNext", {
					date: displayDeliveryOrPickupAt.toFormat("MMMM d"),
					time: displayDeliveryOrPickupAt.toFormat("h:mm a")
				});
			}
		} else {
			Promise.resolve().then(() => {
				throw new Error("Effective deliveryOrPickupAt could not be calculated");
			});

			message = "!!!"; // TODO: handle this?
		}
	}

	return (
		<Typography className={classes.label} component="span">
			{message}
		</Typography>
	);
};

export default ReadyTimeMessage;
